import { observer } from "mobx-react";
import React, { isValidElement, useMemo } from "react";
import { L10n, L10nData } from "@fidget/common/i18n";
import { countryService } from "@fidget/common/services/countries";
import { IGuest } from "../../models/base/Guest";
import { PluginService } from "../../plugins/pluginService";
import { CurrentGuestTime } from "./currentGuestTime";
import styles from "./profileInfo.css";
import classNames from "classnames";
import { BackOfficeField } from "@closerplatform/spinner-openapi";

interface Props {
  profile: IGuest;
}

export const LocationData = observer(({ profile }: Props) => {
  const { languageLocale, zoneId, location } = profile;
  const country = languageLocale && countryService.countryByIso(languageLocale.toUpperCase());
  const countryName = country && country.name;

  return (
    <div className={styles.info}>
      {languageLocale && (
        <div className={styles["info-row"]}>
          <div className={classNames(styles["info-cell"], styles["info-key"])}>
            <L10n id={"clientInfo.language.key"} />
          </div>
          <div className={styles["info-cell"]}>
            <L10n
              id={"clientInfo.language.value"}
              values={{ languageLocale: countryName || languageLocale.toUpperCase() }}
            />
          </div>
        </div>
      )}
      {zoneId && (
        <>
          <div className={styles["info-row"]}>
            <div className={classNames(styles["info-cell"], styles["info-key"])}>
              <L10n id={"clientInfo.timezone.key"} />
            </div>
            <div className={styles["info-cell"]}>
              <L10n id={"clientInfo.timezone.value"} values={{ zoneId }} />
            </div>
          </div>
          <div className={styles["info-row"]}>
            <div className={classNames(styles["info-cell"], styles["info-key"])}>
              <L10n id={"clientInfo.currentTime.key"} />
            </div>
            <div className={styles["info-cell"]}>
              <CurrentGuestTime zoneId={zoneId} />
            </div>
          </div>
        </>
      )}
      {location && (
        <div className={styles["info-row"]}>
          <div className={classNames(styles["info-cell"], styles["info-key"])}>
            <L10n id={"clientInfo.country.key"} />
          </div>
          <div className={styles["info-cell"]}>
            <L10n id={"clientInfo.country.value"} values={{ location }} />
          </div>
        </div>
      )}
    </div>
  );
});

export const BrowserData = observer(({ profile }: Props) => {
  const { browserData } = profile;
  const translationMapping = new Map<string, L10nData & any>([
    ["browserName", "clientInfo.browserName"],
    ["browserVersion", "clientInfo.browserVersion"],
    ["currentPage", "clientInfo.currentPage"],
    ["screenSize", "clientInfo.screenSize"],
    ["browserSize", "clientInfo.browserSize"],
    ["operatingSystem", "clientInfo.operatingSystem"]
  ]);

  const browserDataMap = browserData
    ? browserData.reduce(
        (previous, current) => previous.set(current.key, current.value),
        new Map<string, string>()
      )
    : new Map();

  return (
    <div className={styles.info}>
      {browserDataMap &&
        Array.from(browserDataMap.keys())
          .filter((value) => value !== "currentPageTitle")
          .map((key) => {
            const keyInTranslation = translationMapping.get(key);
            const keyContent =
              keyInTranslation !== undefined ? <L10n id={translationMapping.get(key)} /> : key;
            return (
              <div className={styles["info-row"]} key={key}>
                <div className={classNames(styles["info-cell"], styles["info-key"])}>
                  {keyContent}:
                </div>
                <div className={styles["info-cell"]}>
                  {(key === "currentPage" && (
                    <a href={browserDataMap.get("currentPage")} target="_blank">
                      {browserDataMap.get("currentPageTitle") || browserDataMap.get("currentPage")}
                    </a>
                  )) ||
                    browserDataMap.get(key)}
                </div>
              </div>
            );
          })}
    </div>
  );
});

type RenderableType = React.ReactNode;

export const anyRenderableData = (backOfficeData: Array<BackOfficeField>): boolean =>
  backOfficeData.some((field) => shouldBeRendered(field.value));

const shouldBeRendered = (value: any): boolean => {
  return (
    isValidElement(value) ||
    (Array.isArray(value) && value.every(shouldBeRendered)) ||
    typeof value === "string" ||
    typeof value === "boolean" ||
    !isNaN(value)
  );
};

export const BackofficeData = observer(({ profile }: Props) => {
  const pluginService = useMemo(() => PluginService.getInstance(), []);
  const { additionalData } = profile;
  const additionalDataMap = additionalData ? pluginService.interpretUserData(profile) : [];

  const renderValue = (value?: RenderableType | null): React.ReactNode => {
    if (Array.isArray(value)) {
      return value.join(",");
    }
    return value;
  };

  return (
    <div className={styles.info}>
      {additionalDataMap.map((field) => {
        if (!shouldBeRendered(field.value)) {
          return undefined;
        }

        return (
          <div className={styles["info-row"]} key={field.key}>
            <div className={classNames(styles["info-cell"], styles["info-key"])}>
              {field.displayName || field.key}:
            </div>
            <div className={styles["info-cell"]}>{renderValue(field.value)}</div>
          </div>
        );
      })}
    </div>
  );
});
